import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { httpClient } from '../../utils/apiCall';
import { handleApiError } from '../../utils/apiError';
import { API_ENDPOINTS } from '../../config/api';
import { 
  QuoteDto, 
  QuoteItemDto, 
  UpdateSingleQuoteItemDto,
  UpdateQuoteDto,
  QuotePublicDetailParams
} from '../../generated';

// State interface
export interface QuotePublicState {
  currentQuote: QuoteDto | null;
  quoteItems: QuoteItemDto[];
  loading: {
    detail: boolean;
    itemUpdate: boolean;
    update: boolean;
    rejectItem: boolean;
  };
  error: {
    detail: string | null;
    itemUpdate: string | null;
    update: string | null;
    rejectItem: string | null;
  };
}

// Initial state
const initialState: QuotePublicState = {
  currentQuote: null,
  quoteItems: [],
  loading: {
    detail: false,
    itemUpdate: false,
    update: false,
    rejectItem: false,
  },
  error: {
    detail: null,
    itemUpdate: null,
    update: null,
    rejectItem: null,
  },
};

// Hata çevirimi ortak yardımcıda: iptal edilen istek için null döner (hata sayılmaz).


// Async thunks
export const fetchQuotePublicDetail = createAsyncThunk(
  'quotePublic/fetchQuotePublicDetail',
  async (params: QuotePublicDetailParams, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.QUOTE_PUBLIC.REVIEW_DETAIL}/${params.publicId}`;
      const response = await httpClient.get(url);
      
      // Success kontrolü - eğer success false ise hata döndür
      if (response.data && response.data.success === false) {
        return rejectWithValue('Ulaşılamıyor');
      }
      
      return response;
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to fetch quote public details');
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateQuotePublicItem = createAsyncThunk(
  'quotePublic/updateQuotePublicItem',
  async ({ publicId, quoteItemId, data }: { publicId: string; quoteItemId: number; data: UpdateSingleQuoteItemDto }, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.QUOTE_PUBLIC.REVIEW_ITEMS_UPDATE}/${publicId}/items/${quoteItemId}`;
      const response = await httpClient.patch(url, data);
      return { publicId, quoteItemId, response };
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to update quote public item');
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateQuotePublic = createAsyncThunk(
  'quotePublic/updateQuotePublic',
  async ({ publicId, data }: { publicId: string; data: UpdateQuoteDto }, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.QUOTE_PUBLIC.REVIEW_UPDATE}/${publicId}`;
      const response = await httpClient.patch(url, data);
      return { publicId, response };
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to update quote public');
      return rejectWithValue(errorMessage);
    }
  }
);

export const sendQuotePublicConfirmation = createAsyncThunk(
  'quotePublic/sendConfirmation',
  async (publicId: string, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.QUOTE_PUBLIC.SEND_CONFIRMATION}/${publicId}/send-confirmation`;
      const response = await httpClient.post(url);
      return response;
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to send confirmation email');
      return rejectWithValue(errorMessage);
    }
  }
);

// New endpoint: Quote Public Items Reject
export const rejectQuotePublicItem = createAsyncThunk(
  'quotePublic/rejectQuotePublicItem',
  async ({ publicId, quoteItemId }: { publicId: string; quoteItemId: number }, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.QUOTE_PUBLIC.REVIEW_ITEMS_UPDATE}/${publicId}/items/${quoteItemId}/reject`;
      const response = await httpClient.patch(url);
      return { publicId, quoteItemId, response };
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to reject quote public item');
      return rejectWithValue(errorMessage);
    }
  }
);

// Quote Public slice
const quotePublicSlice = createSlice({
  name: 'quotePublic',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.error = {
        detail: null,
        itemUpdate: null,
        update: null,
        rejectItem: null,
      };
    },
    clearCurrentQuote: (state) => {
      state.currentQuote = null;
      state.quoteItems = [];
    },
    resetQuotePublicState: () => {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    // Fetch Quote Public Detail
    builder
      .addCase(fetchQuotePublicDetail.pending, (state) => {
        state.loading.detail = true;
        state.error.detail = null;
      })
      .addCase(fetchQuotePublicDetail.fulfilled, (state, action) => {
        state.loading.detail = false;
        state.currentQuote = action.payload.data;
        state.quoteItems = action.payload.data?.quoteItems || [];
      })
      .addCase(fetchQuotePublicDetail.rejected, (state, action) => {
        state.loading.detail = false;
        state.error.detail = action.payload as string;
      });

    // Update Quote Public Item
    builder
      .addCase(updateQuotePublicItem.pending, (state) => {
        state.loading.itemUpdate = true;
        state.error.itemUpdate = null;
      })
      .addCase(updateQuotePublicItem.fulfilled, (state) => {
        state.loading.itemUpdate = false;
        // Note: In a real implementation, you might want to refetch the quote detail
        // to get the updated items from the server
      })
      .addCase(updateQuotePublicItem.rejected, (state, action) => {
        state.loading.itemUpdate = false;
        state.error.itemUpdate = action.payload as string;
      });

    // Update Quote Public
    builder
      .addCase(updateQuotePublic.pending, (state) => {
        state.loading.update = true;
        state.error.update = null;
      })
      .addCase(updateQuotePublic.fulfilled, (state) => {
        state.loading.update = false;
        // Note: In a real implementation, you might want to refetch the quote detail
        // to get the updated quote from the server
      })
      .addCase(updateQuotePublic.rejected, (state, action) => {
        state.loading.update = false;
        state.error.update = action.payload as string;
      });

    // Reject Quote Public Item
    builder
      .addCase(rejectQuotePublicItem.pending, (state) => {
        state.loading.rejectItem = true;
        state.error.rejectItem = null;
      })
      .addCase(rejectQuotePublicItem.fulfilled, (state, action) => {
        state.loading.rejectItem = false;
        // Update quote item status in the store
        if (state.currentQuote && state.currentQuote.quoteItems) {
          const itemIndex = state.currentQuote.quoteItems.findIndex(item => item.id === action.payload.quoteItemId);
          if (itemIndex !== -1) {
            state.currentQuote.quoteItems[itemIndex]!.decisionStatus = 'Rejected';
          }
        }
      })
      .addCase(rejectQuotePublicItem.rejected, (state, action) => {
        state.loading.rejectItem = false;
        state.error.rejectItem = action.payload as string;
      });
  },
});

// Export actions
export const {
  clearErrors,
  clearCurrentQuote,
  resetQuotePublicState,
} = quotePublicSlice.actions;

// Export reducer
export default quotePublicSlice.reducer;

// Selectors
export const selectQuotePublicCurrentQuote = (state: { quotePublic: QuotePublicState }) => state.quotePublic.currentQuote;
export const selectQuotePublicQuoteItems = (state: { quotePublic: QuotePublicState }) => state.quotePublic.quoteItems;
export const selectQuotePublicLoading = (state: { quotePublic: QuotePublicState }) => state.quotePublic.loading;
export const selectQuotePublicError = (state: { quotePublic: QuotePublicState }) => state.quotePublic.error;

// Loading selectors
export const selectIsQuotePublicDetailLoading = (state: { quotePublic: QuotePublicState }) => state.quotePublic.loading.detail;
export const selectIsQuotePublicItemUpdateLoading = (state: { quotePublic: QuotePublicState }) => state.quotePublic.loading.itemUpdate;
export const selectIsQuotePublicUpdateLoading = (state: { quotePublic: QuotePublicState }) => state.quotePublic.loading.update;
export const selectIsQuotePublicRejectItemLoading = (state: { quotePublic: QuotePublicState }) => state.quotePublic.loading.rejectItem;

// Error selectors
export const selectQuotePublicDetailError = (state: { quotePublic: QuotePublicState }) => state.quotePublic.error.detail;
export const selectQuotePublicItemUpdateError = (state: { quotePublic: QuotePublicState }) => state.quotePublic.error.itemUpdate;
export const selectQuotePublicUpdateError = (state: { quotePublic: QuotePublicState }) => state.quotePublic.error.update;
export const selectQuotePublicRejectItemError = (state: { quotePublic: QuotePublicState }) => state.quotePublic.error.rejectItem;
